import {
  AssistantStep,
  ChatItem,
  SourceMetadata,
  ToolCallStatus,
} from "features/assistant/definitions";

export interface ToolConfirmationRequest {
  requestId: string;
  toolName: string;
  description: string;
}

export type FeedbackValue = "THUMBS_UP" | "THUMBS_DOWN";

export interface ChatState {
  items: ChatItem[];
  // Source metadata per message id (e.g. Answers citations). Message-level, not per segment.
  messageMeta: Record<string, SourceMetadata>;
  messageSteps: Record<string, AssistantStep[]>;
  isConnected: boolean;
  isLoading: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  inlineError: string | null;
  pendingConfirmation: ToolConfirmationRequest | null;
  pendingMessageId: string | null;
  sessionFeedback: FeedbackValue | null;
}

export type ChatAction =
  | { type: "SET_CONNECTED"; connected: boolean }
  | { type: "SET_LOADING"; loading: boolean }
  | {
      type: "ADD_MESSAGE";
      id: string;
      role: "user" | "assistant";
      content: string;
      isStreaming: boolean;
      displayContent?: string;
    }
  | {
      type: "APPEND_DELTA";
      messageId: string;
      delta: string;
    }
  | {
      type: "MARK_COMPLETE";
      messageId: string;
      sourceMetadata?: SourceMetadata;
    }
  | { type: "CLEAR_MESSAGES" }
  | { type: "SET_ERROR"; error: string | null }
  | {
      type: "SET_PENDING_CONFIRMATION";
      confirmation: ToolConfirmationRequest | null;
    }
  | {
      type: "SET_ITEMS";
      items: ChatItem[];
      messageMeta: Record<string, SourceMetadata>;
    }
  | { type: "SET_LOADING_HISTORY"; loading: boolean }
  | { type: "RESET" }
  | {
      type: "ADD_TOOL_CALL";
      toolUseId: string;
      toolName: string;
    }
  | {
      type: "COMPLETE_TOOL_CALL";
      toolUseId: string;
      status: "completed" | "error";
    }
  | {
      type: "SET_PENDING_MESSAGE_ID";
      messageId: string | null;
      sourceMetadata?: SourceMetadata;
    }
  | {
      type: "APPEND_STEP";
      messageId: string;
      stepName: string;
      status: AssistantStep["status"];
    }
  | { type: "SET_INLINE_ERROR"; message: string | null }
  | { type: "SET_SESSION_FEEDBACK"; feedback: FeedbackValue | null };

const sealStreamingMessage = (items: ChatItem[]): ChatItem[] => {
  const last = items[items.length - 1];
  if (last?.type === "message" && last.isStreaming) {
    return [...items.slice(0, -1), { ...last, isStreaming: false }];
  }
  return items;
};

export const chatReducer = (
  state: ChatState,
  action: ChatAction,
): ChatState => {
  switch (action.type) {
    case "SET_CONNECTED":
      return {
        ...state,
        isConnected: action.connected,
        ...(action.connected
          ? {}
          : {
              items: [],
              messageMeta: {},
              messageSteps: {},
              isLoading: false,
              pendingConfirmation: null,
              pendingMessageId: null,
            }),
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.loading };
    case "ADD_MESSAGE":
      return {
        ...state,
        items: [
          ...state.items,
          {
            type: "message",
            id: action.id,
            messageId: action.id,
            role: action.role,
            content: action.content,
            isStreaming: action.isStreaming,
            displayContent: action.displayContent,
          },
        ],
      };
    case "APPEND_DELTA": {
      const last = state.items[state.items.length - 1];
      if (
        last?.type === "message" &&
        last.messageId === action.messageId &&
        last.isStreaming
      ) {
        return {
          ...state,
          items: [
            ...state.items.slice(0, -1),
            { ...last, content: last.content + action.delta },
          ],
        };
      }
      if (state.pendingMessageId === action.messageId) {
        const seq = state.items.filter(
          (item) =>
            item.type === "message" && item.messageId === action.messageId,
        ).length;
        const id = seq === 0 ? action.messageId : `${action.messageId}-${seq}`;
        return {
          ...state,
          items: [
            ...state.items,
            {
              type: "message",
              id,
              messageId: action.messageId,
              role: "assistant",
              content: action.delta,
              isStreaming: true,
            },
          ],
        };
      }
      return state;
    }
    case "MARK_COMPLETE":
      // The resolved source metadata (e.g. Answers citations, empty on the opening deltas) is a
      // message-level fact, so store it against the message id rather than any one segment.
      return {
        ...state,
        pendingMessageId: null,
        messageMeta: action.sourceMetadata
          ? { ...state.messageMeta, [action.messageId]: action.sourceMetadata }
          : state.messageMeta,
        items: state.items.map((item) =>
          item.type === "message" && item.messageId === action.messageId
            ? { ...item, isStreaming: false }
            : item,
        ),
      };
    case "CLEAR_MESSAGES":
      return {
        ...state,
        items: [],
        messageMeta: {},
        messageSteps: {},
        pendingConfirmation: null,
        pendingMessageId: null,
      };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "SET_PENDING_CONFIRMATION":
      return { ...state, pendingConfirmation: action.confirmation };
    case "SET_ITEMS":
      return {
        ...state,
        items: action.items,
        messageMeta: action.messageMeta,
        messageSteps: {},
        isLoadingHistory: false,
        error: null,
        pendingMessageId: null,
      };
    case "SET_LOADING_HISTORY":
      return { ...state, isLoadingHistory: action.loading };
    case "RESET":
      return initialChatState;
    case "ADD_TOOL_CALL":
      return {
        ...state,
        items: [
          ...sealStreamingMessage(state.items),
          {
            type: "tool_call",
            id: crypto.randomUUID(),
            toolUseId: action.toolUseId,
            toolName: action.toolName,
            status: "running" as ToolCallStatus,
          },
        ],
      };
    case "COMPLETE_TOOL_CALL":
      return {
        ...state,
        items: state.items.map((item) =>
          item.type === "tool_call" && item.toolUseId === action.toolUseId
            ? { ...item, status: action.status as ToolCallStatus }
            : item,
        ),
      };
    case "SET_PENDING_MESSAGE_ID":
      // Seed the message's source at TEXT_START so the badge/accent shows while streaming;
      // MARK_COMPLETE later upgrades it with the resolved citations.
      return {
        ...state,
        pendingMessageId: action.messageId,
        messageMeta:
          action.messageId && action.sourceMetadata
            ? {
                ...state.messageMeta,
                [action.messageId]: action.sourceMetadata,
              }
            : state.messageMeta,
      };
    case "APPEND_STEP": {
      const existing = state.messageSteps[action.messageId] ?? [];
      const idx = existing.findIndex((s) => s.stepName === action.stepName);
      const next =
        idx >= 0
          ? existing.map((s, i) =>
              i === idx ? { ...s, status: action.status } : s,
            )
          : [...existing, { stepName: action.stepName, status: action.status }];
      const hasItem = state.items.some(
        (item) =>
          item.type === "message" && item.messageId === action.messageId,
      );
      const items = hasItem
        ? state.items
        : [
            ...state.items,
            {
              type: "message" as const,
              id: action.messageId,
              messageId: action.messageId,
              role: "assistant" as const,
              content: "",
              isStreaming: true,
            },
          ];
      const messageMeta = state.messageMeta[action.messageId]
        ? state.messageMeta
        : {
            ...state.messageMeta,
            [action.messageId]: { source: "answers" as const },
          };
      return {
        ...state,
        items,
        messageMeta,
        messageSteps: { ...state.messageSteps, [action.messageId]: next },
      };
    }
    case "SET_INLINE_ERROR":
      return { ...state, inlineError: action.message };
    case "SET_SESSION_FEEDBACK":
      return { ...state, sessionFeedback: action.feedback };
    default:
      return state;
  }
};

export const initialChatState: ChatState = {
  items: [],
  messageMeta: {},
  messageSteps: {},
  isConnected: false,
  isLoading: false,
  isLoadingHistory: false,
  error: null,
  inlineError: null,
  pendingConfirmation: null,
  pendingMessageId: null,
  sessionFeedback: null,
};
